import { KeyRound, ListOrdered, ShieldCheck } from "lucide-mui";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useState } from "react";
import { userHasGlobalScope } from "../../auth/scopes.ts";
import GoogleLogo from "../../components/GoogleLogo.tsx";
import Panel from "../../components/Panel.tsx";
import RelativeTime from "../../components/RelativeTime.tsx";
import SettingRow from "../../components/SettingRow.tsx";
import { StatusLabel } from "../../components/StatusIcon.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import ChangePasswordDialog from "./components/ChangePasswordDialog.tsx";
import DisableMfaDialog from "./components/DisableMfaDialog.tsx";
import MfaSetupDialog from "./components/MfaSetupDialog.tsx";
import RegenerateBackupCodesDialog from "./components/RegenerateBackupCodesDialog.tsx";
import SessionsSection from "./components/SessionsSection.tsx";

const FEW_CODES = 3;

type Dialog = "password" | "mfa-setup" | "mfa-disable" | "backup-codes" | null;

export default function SecurityPage() {
  const { user } = useAuth();
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);

  if (!user) return null;

  const canManageMfa = userHasGlobalScope(user, "auth:manage_mfa");
  const remaining = user.mfa_backup_codes_remaining ?? 0;

  return (
    <Stack spacing={3}>
      <Panel title="Sign-in" subtitle="How you prove it's you" disablePadding>
        <SettingRow
          icon={<KeyRound />}
          label="Password"
          description={
            !user.has_password ? (
              "You sign in with Google, so there's no password to change"
            ) : user.password_changed_at ? (
              <>
                Last changed <RelativeTime value={user.password_changed_at} />
              </>
            ) : (
              "Set when you joined"
            )
          }
          action={
            user.has_password && (
              <Button size="small" variant="outlined" onClick={() => setDialog("password")}>
                Change password
              </Button>
            )
          }
        />
        {user.auth_provider === "google" && (
          <SettingRow
            icon={<GoogleLogo />}
            label="Google"
            description="Sign in with your Google Workspace account"
            action={<StatusLabel kind="ok" label="Linked" />}
          />
        )}
      </Panel>

      {canManageMfa && (
        <Panel
          title="Two-step verification"
          subtitle="A code from your phone on top of your password or Google sign-in"
          disablePadding
        >
          <SettingRow
            icon={<ShieldCheck />}
            label="Authenticator app"
            description={
              user.mfa_enabled
                ? "Signing in asks for a code from your app"
                : "Off. Turn it on so a stolen password isn't enough to get in."
            }
            action={
              user.mfa_enabled ? (
                <>
                  <StatusLabel kind="ok" label="On" />
                  <Button size="small" color="error" onClick={() => setDialog("mfa-disable")}>
                    Turn off
                  </Button>
                </>
              ) : (
                <Button size="small" variant="contained" onClick={() => setDialog("mfa-setup")}>
                  Turn on
                </Button>
              )
            }
          />
          {user.mfa_enabled && (
            <SettingRow
              icon={<ListOrdered />}
              label="Backup codes"
              description={
                remaining === 0
                  ? "None left. Generate new ones so you can still get in without your phone."
                  : `${remaining} of 10 left, for when you can't use your phone`
              }
              action={
                <>
                  {remaining <= FEW_CODES && <StatusLabel kind="attention" label={remaining === 0 ? "None left" : "Running low"} />}
                  <Button size="small" variant="outlined" onClick={() => setDialog("backup-codes")}>
                    Generate new codes
                  </Button>
                </>
              }
            />
          )}
        </Panel>
      )}

      <SessionsSection />

      <ChangePasswordDialog open={dialog === "password"} onClose={close} />
      {dialog === "mfa-setup" && <MfaSetupDialog onClose={close} />}
      {dialog === "mfa-disable" && <DisableMfaDialog onClose={close} />}
      {dialog === "backup-codes" && <RegenerateBackupCodesDialog onClose={close} />}
    </Stack>
  );
}
